import {ChangeDetectionStrategy, ChangeDetectorRef, Component} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';

import {EMPTY, of, Subject} from 'rxjs';
import {catchError, groupBy, map, mergeMap, switchMap, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../../destroyable.component';
import {Employee} from '../../employees/employees.model';
import {EmployeesService} from '../../employees/employees.service';
import {SignatureData, SignatureInfo, SignatureMethodEnum} from '../../files/files.model';
import {Value, ZoneValues} from '../../form-generator/form-generator.interfaces';
import {FormDescriptorService} from '../../form-generator/form-descriptor.service';
import {Validators as GpValidators} from '../../forms/common-validators';
import {ModalService} from '../../modal';
import {User, UserService} from '../../user';
import {EmployeeDocument} from '../documents.interface';
import {DocumentsService, SignatureMethodDto} from '../documents.service';
import {
  getAvailableSignatureMethods,
  sortByOptionOrder
} from '../signature/signature-method.util';

export const ASK_FOR_SIGNATURE_MODAL_ID = 'ask-for-signature';

interface SignatureSelection {
  employee: number | null;
  email: string;
  phoneCountryCode: string;
  phoneCountryCodeOptions: PhoneCountryCodeOption[];
  phoneNumber: string;
  firstName: string;
  lastName: string;
  language: string;
  contactLoaded: boolean;
}

interface PhoneCountryCodeOption {
  value: string;
  display: string;
}

interface PhoneCountryCodeSelection {
  id?: string | number;
  value?: string | number;
}

interface EmployeeDataContainer {
  empDatas?: ZoneValues[];
  cliRegID?: number;
  mainContract?: boolean;
  attributes?: EmployeeDataContainer;
}

interface EmployeeLookup {
  index: number;
  employeeId: number;
  generation: number;
}

@Component({
  selector: 'gp-ask-for-signature-popup',
  templateUrl: './ask-for-signature-popup.component.html',
  styleUrls: ['./ask-for-signature-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AskForSignaturePopupComponent extends Destroyable {

  private static readonly E164_PHONE_PATTERN = /^\+[1-9]\d{1,14}$/;
  private static readonly SIGNATURE_METHOD_ORDER: SignatureMethodEnum[] = [
    SignatureMethodEnum.MANUAL,
    SignatureMethodEnum.SMSOTP,
    SignatureMethodEnum.MAILOTP,
    SignatureMethodEnum.ITSME,
    SignatureMethodEnum.BEID,
    SignatureMethodEnum.ITSMEE2E
  ];

  public SignatureMethodEnum = SignatureMethodEnum;
  public signatureMethodOptions: SignatureMethodEnum[] = [SignatureMethodEnum.MANUAL];
  public selectedSignatureMethods: SignatureMethodEnum[] = [];
  public signatureDocument: EmployeeDocument;
  public signatureRoles: string[] = [];
  public signatureSelections: SignatureSelection[] = [];
  public signatureOrderMode: 'UNORDERED' | 'ORDERED' = 'UNORDERED';
  public draggedSignerRoleIndex: number | null = null;
  public signatureSubmitting = false;
  public popupContentVisible = false;

  /**
   * The modal instance is reused for every document, so contact lookups started for a previously
   * opened document must not be applied to the selections rebuilt by a later {@link open} call.
   */
  private openGeneration = 0;
  private employeeLookup$: Subject<EmployeeLookup> = new Subject<EmployeeLookup>();

  constructor(public documentsService: DocumentsService,
              private modalService: ModalService,
              private userService: UserService,
              private employeesService: EmployeesService,
              private formDescriptorService: FormDescriptorService,
              private translate: TranslateService,
              private toastrService: ToastrService,
              private cd: ChangeDetectorRef) {
    super();
    this.employeeLookup$.pipe(
      groupBy((lookup: EmployeeLookup) => lookup.index),
      mergeMap((lookupsOfRow) => lookupsOfRow.pipe(
        switchMap((lookup: EmployeeLookup) => this.userService.getUserById(lookup.employeeId).pipe(
          switchMap((user: User) => user.cliEmpId
            ? this.employeesService.getEmployee(user.cliEmpId).pipe(
              map((employee: Employee) => ({user, employee})),
              catchError(() => of({user, employee: null}))
            )
            : of({user, employee: null})),
          switchMap(({user, employee}) => {
            const cliRegId = this.getEmployeeCliRegId(employee);
            return cliRegId
              ? this.formDescriptorService.getEmpModelZoneDescriptors(cliRegId).pipe(
                map((descriptors) => ({
                  user,
                  employee,
                  options: descriptors.get('MainPhoneCountryCode')?.metadatas?.items ?? []
                })),
                catchError(() => of({user, employee, options: []}))
              )
              : of({user, employee, options: []});
          }),
          tap(({user, employee, options}) => this.applyEmployeeContact(lookup, user, employee, options)),
          catchError(() => {
            this.toastrService.error(this.translate.instant('DOCUMENTS.signature_user_load_failed'));
            this.cd.detectChanges();
            return EMPTY;
          })
        ))
      )),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public open(document: EmployeeDocument, cliRegId: number): void {
    this.documentsService.getSignatureMethods(cliRegId).pipe(
      tap((signatureMethods: SignatureMethodDto[]) => {
        this.destroyPopupContent();
        this.openGeneration++;
        const availableSignatureMethods = getAvailableSignatureMethods(signatureMethods);
        this.signatureMethodOptions = AskForSignaturePopupComponent.SIGNATURE_METHOD_ORDER
          .filter((method) => availableSignatureMethods.includes(method));
        const signatureInfos = [...(document.signatureData?.signatureInfos ?? [])];
        this.signatureDocument = {
          ...document,
          signatureData: {...document.signatureData, signatureInfos}
        };
        this.signatureRoles = this.getDistinctRoles(signatureInfos);
        this.signatureOrderMode = this.signatureRoles
          .some((role) => this.getSigningOrder(signatureInfos, role) > 0) ? 'ORDERED' : 'UNORDERED';
        this.selectedSignatureMethods = this.seedSelectedSignatureMethods(signatureInfos);
        this.signatureSelections = this.signatureRoles
          .map(() => ({
            employee: null,
            email: '',
            phoneCountryCode: '',
            phoneCountryCodeOptions: [],
            phoneNumber: '',
            firstName: '',
            lastName: '',
            language: 'en',
            contactLoaded: false
          }));
        this.popupContentVisible = true;
        this.modalService.openModalWithId(ASK_FOR_SIGNATURE_MODAL_ID);
        this.cd.detectChanges();
      }),
      catchError(() => {
        this.toastrService.error(this.translate.instant('DOCUMENTS.signature_methods_load_failed'));
        this.cd.detectChanges();
        return EMPTY;
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public get isSignatureFormValid(): boolean {
    return this.signatureSelections.length > 0
      && this.selectedSignatureMethods.length > 0
      && this.signatureSelections.every((selection) => !!selection.employee
        && selection.contactLoaded
        && this.isEmailValid(selection.email)
        && (!this.isSmsOtpSelected || this.isPhoneNumberValid(selection)));
  }

  public get isSmsOtpSelected(): boolean {
    return this.selectedSignatureMethods.includes(SignatureMethodEnum.SMSOTP);
  }

  public get assignedSignerCount(): number {
    return this.signatureSelections.filter((selection) => !!selection.employee).length;
  }

  public isEmailValid(email: string): boolean {
    return !!email?.trim() && !GpValidators.emailValidator(new UntypedFormControl(email.trim()));
  }

  public isPhoneNumberValid(selection: SignatureSelection): boolean {
    return AskForSignaturePopupComponent.E164_PHONE_PATTERN.test(this.formatPhoneNumber(selection));
  }

  public confirmSignature(modalRef: { close: () => void }): void {
    if (!this.isSignatureFormValid) {
      return;
    }

    const signatureInfos = this.signatureDocument?.signatureData?.signatureInfos ?? [];

    this.signatureSubmitting = true;
    const updatedInfos: SignatureInfo[] = signatureInfos.map((info) => {
      const roleIndex = this.signatureRoles.indexOf(info.markerName);
      const selection = this.signatureSelections[roleIndex];
      return {
        ...info,
        signatureMethods: [...this.selectedSignatureMethods],
        signer: {
          firstName: selection.firstName,
          lastName: selection.lastName,
          emailAddress: selection.email,
          phoneNumber: this.selectedSignatureMethods.includes(SignatureMethodEnum.SMSOTP)
            ? this.formatPhoneNumber(selection)
            : null,
          language: selection.language,
          birthDate: null,
          redirectUrl: null,
          signingOrder: this.signatureOrderMode === 'ORDERED' ? roleIndex : 0
        }
      };
    });
    const signatureData: SignatureData = {
      ...this.signatureDocument.signatureData,
      signatureInfos: updatedInfos
    };
    this.documentsService.initiateSignature(this.signatureDocument.id, signatureData).pipe(
      tap(() => {
        this.signatureSubmitting = false;
        modalRef.close();
        this.documentsService.refreshDocumentList$.next(true);
        this.toastrService.success(this.translate.instant('DOCUMENTS.signature_request_sent'));
        this.cd.detectChanges();
      }),
      catchError(() => {
        this.signatureSubmitting = false;
        this.toastrService.error(this.translate.instant('DOCUMENTS.signature_request_failed'));
        this.cd.detectChanges();
        return EMPTY;
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public updateSignatureSelection(index: number, field: 'employee', value: any): void {
    this.signatureSelections = this.signatureSelections.map((sel, i) =>
      i === index ? {
        ...sel,
        [field]: value,
        email: '',
        phoneCountryCode: '',
        phoneCountryCodeOptions: [],
        phoneNumber: '',
        firstName: '',
        lastName: '',
        language: 'en',
        contactLoaded: false
      } : sel
    );
    if (!value) {
      return;
    }
    this.employeeLookup$.next({index, employeeId: value, generation: this.openGeneration});
  }

  private applyEmployeeContact(lookup: EmployeeLookup,
                               user: User,
                               employee: Employee | null,
                               options: PhoneCountryCodeOption[]): void {
    if (lookup.generation !== this.openGeneration
      || this.signatureSelections[lookup.index]?.employee !== lookup.employeeId) {
      return;
    }
    const empDatas = this.getEmployeeData(employee);
    const countryCode = this.getEmployeeZoneValue(empDatas, 'MainPhoneCountryCode');
    const phoneCountryCodeOptions = [...options];
    if (countryCode?.value && !phoneCountryCodeOptions.some((option) =>
      String(option.value) === String(countryCode.value))) {
      phoneCountryCodeOptions.push({
        value: String(countryCode.value),
        display: countryCode.transId ?? `+${countryCode.value}`
      });
    }
    phoneCountryCodeOptions.sort((first, second) => first.display.localeCompare(second.display));
    this.signatureSelections = this.signatureSelections.map((sel, i) =>
      i === lookup.index ? {
        ...sel,
        email: user.email ?? '',
        phoneCountryCode: countryCode?.value ? String(countryCode.value) : '',
        phoneCountryCodeOptions,
        phoneNumber: this.getEmployeeZoneValue(empDatas, 'MainPhone')?.value?.toString() ?? '',
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        language: user.language ?? 'en',
        contactLoaded: true
      } : sel
    );
    this.cd.detectChanges();
  }

  private getEmployeeCliRegId(employee: Employee | null): number | null {
    const contracts: EmployeeDataContainer[] = [
      ...(employee?.attributes?.empContracts ?? []),
      ...((employee?.included ?? []) as EmployeeDataContainer[])
    ].filter((candidate) => candidate.cliRegID || candidate.attributes?.cliRegID);
    const contract = contracts.find((candidate) =>
      candidate.mainContract || candidate.attributes?.mainContract) ?? contracts[0];
    return contract?.cliRegID ?? contract?.attributes?.cliRegID ?? null;
  }

  private getEmployeeData(employee: Employee | null): ZoneValues[] {
    if (!employee) {
      return [];
    }
    const root = employee.attributes as typeof employee.attributes & EmployeeDataContainer;
    const contracts: EmployeeDataContainer[] = employee.attributes.empContracts ?? [];
    const included: EmployeeDataContainer[] = employee.included ?? [];
    return [
      ...(root.empDatas ?? []),
      ...contracts.flatMap((contract) => contract.empDatas ?? contract.attributes?.empDatas ?? []),
      ...included.flatMap((contract) => contract.empDatas ?? contract.attributes?.empDatas ?? [])
    ];
  }

  private getEmployeeZoneValue(empDatas: ZoneValues[], zoneName: string): Value | null {
    for (const empData of empDatas) {
      const value = (empData._values ?? empData.values)?.[zoneName];
      if (value) {
        return value;
      }
    }
    return null;
  }

  private formatPhoneNumber(selection: SignatureSelection): string {
    const callingCode = selection.phoneCountryCode?.replace(/\D/g, '') ?? '';
    const phoneNumber = selection.phoneNumber?.replace(/\D/g, '') ?? '';
    return callingCode && phoneNumber ? `+${callingCode}${phoneNumber}` : '';
  }

  public updateSignatureContact(index: number, field: 'email' | 'phoneNumber', value: string): void {
    this.signatureSelections = this.signatureSelections.map((sel, i) =>
      i === index ? {...sel, [field]: value} : sel
    );
  }

  public updatePhoneNumber(index: number, phoneNumber: string): void {
    this.signatureSelections = this.signatureSelections.map((selection, selectionIndex) =>
      selectionIndex === index ? {...selection, phoneNumber} : selection
    );
  }

  public updatePhoneCountryCode(index: number, option: PhoneCountryCodeSelection): void {
    const value = option?.value ?? option?.id;
    this.signatureSelections = this.signatureSelections.map((selection, selectionIndex) =>
      selectionIndex === index ? {...selection, phoneCountryCode: value ? String(value) : ''} : selection
    );
  }

  public getActivePhoneCountryCode(selection: SignatureSelection): PhoneCountryCodeOption[] {
    const active = selection?.phoneCountryCodeOptions
      .find((option) => String(option.value) === selection.phoneCountryCode);
    return active ? [{...active, display: `+${active.value}`}] : [];
  }

  public positionPhoneCountryCodeDropdown = (_viewportHeight: number,
                                             dropdown: HTMLElement,
                                             mainElement: HTMLElement): void => {
    const menu = dropdown.firstElementChild as HTMLElement;
    const scrollContainer = mainElement.closest('.signature-popup') as HTMLElement;
    if (!menu || !scrollContainer) {
      return;
    }
    const edgeGap = 4;
    const triggerRect = mainElement.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();
    const spaceAbove = triggerRect.top - containerRect.top - edgeGap;
    const spaceBelow = containerRect.bottom - triggerRect.bottom - edgeGap;
    const openAbove = spaceAbove > spaceBelow;
    const choices = menu.querySelector('.ui-select-choices') as HTMLElement;
    if (choices) {
      choices.style.maxHeight = `${Math.max(openAbove ? spaceAbove : spaceBelow, 0)}px`;
    }
    menu.style.top = openAbove
      ? `${-menu.getBoundingClientRect().height - triggerRect.height}px`
      : '';
  };

  public selectSignatureMethod(method: SignatureMethodEnum): void {
    if (!this.signatureMethodOptions.includes(method)) {
      return;
    }
    if (this.selectedSignatureMethods.includes(method)) {
      if (this.selectedSignatureMethods.length === 1) {
        return;
      }
      this.selectedSignatureMethods = this.selectedSignatureMethods.filter((selected) => selected !== method);
    } else {
      this.selectedSignatureMethods = sortByOptionOrder(
        this.signatureMethodOptions,
        [...this.selectedSignatureMethods, method]
      );
    }
    this.cd.detectChanges();
  }

  public changeSignatureOrderMode(ordered: boolean): void {
    this.signatureOrderMode = ordered ? 'ORDERED' : 'UNORDERED';
  }

  public moveSignatureUp(index: number): void {
    if (index <= 0) {
      return;
    }
    this.swapSignatureRoles(index, index - 1);
  }

  public moveSignatureDown(index: number): void {
    if (index >= this.signatureRoles.length - 1) {
      return;
    }
    this.swapSignatureRoles(index, index + 1);
  }

  public startDraggingSignerRole(index: number, event: DragEvent): void {
    if (this.signatureOrderMode !== 'ORDERED') {
      event.preventDefault();
      return;
    }
    this.draggedSignerRoleIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    }
  }

  public allowSignerRoleDrop(event: DragEvent): void {
    if (this.signatureOrderMode !== 'ORDERED') {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  public dropSignerRole(index: number, event: DragEvent): void {
    event.preventDefault();
    if (this.signatureOrderMode !== 'ORDERED'
      || this.draggedSignerRoleIndex === null
      || this.draggedSignerRoleIndex === index) {
      this.draggedSignerRoleIndex = null;
      return;
    }
    this.moveSignatureRole(this.draggedSignerRoleIndex, index);
    this.draggedSignerRoleIndex = null;
  }

  public stopDraggingSignerRole(): void {
    this.draggedSignerRoleIndex = null;
  }

  /**
   * The modal is only hidden and never destroyed, so the previous content - and especially the
   * employee dropdowns holding their own selection state - has to be thrown away before the popup
   * is filled in again.
   */
  private destroyPopupContent(): void {
    this.popupContentVisible = false;
    this.cd.detectChanges();
  }

  private getDistinctRoles(signatureInfos: SignatureInfo[]): string[] {
    const roles = signatureInfos.reduce((acc: string[], info: SignatureInfo) =>
      acc.includes(info.markerName) ? acc : [...acc, info.markerName], []);
    return roles
      .map((role, index) => ({role, index, order: this.getSigningOrder(signatureInfos, role)}))
      .sort((a, b) => (a.order - b.order) || (a.index - b.index))
      .map((entry) => entry.role);
  }

  private getSigningOrder(signatureInfos: SignatureInfo[], role: string): number {
    const info = signatureInfos.find((candidate) => candidate.markerName === role);
    return info?.signer?.signingOrder ?? 0;
  }

  private seedSelectedSignatureMethods(signatureInfos: SignatureInfo[]): SignatureMethodEnum[] {
    const stored = new Set<SignatureMethodEnum>();
    (signatureInfos ?? []).forEach((info) =>
      (info.signatureMethods ?? []).forEach((method) => stored.add(method)));
    const available = sortByOptionOrder(this.signatureMethodOptions, [...stored]);
    return available.length ? available : [...this.signatureMethodOptions];
  }

  private moveSignatureRole(fromIndex: number, toIndex: number): void {
    const roles = [...this.signatureRoles];
    const selections = [...this.signatureSelections];
    const [role] = roles.splice(fromIndex, 1);
    const [selection] = selections.splice(fromIndex, 1);
    roles.splice(toIndex, 0, role);
    selections.splice(toIndex, 0, selection);
    this.signatureRoles = roles;
    this.signatureSelections = selections;
    this.cd.markForCheck();
  }

  private swapSignatureRoles(from: number, to: number): void {
    [this.signatureRoles[from], this.signatureRoles[to]] =
      [this.signatureRoles[to], this.signatureRoles[from]];
    [this.signatureSelections[from], this.signatureSelections[to]] =
      [this.signatureSelections[to], this.signatureSelections[from]];
  }
}
